import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { returnDecimalFormatting } from "@/lib/globalFormatterHelper";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
// Circular with this file; only accessed at call time inside quoteValueFormatter,
// which ESM live bindings allow. Do not access at module scope.
import { getNotationForMainTable } from "../actionSummaryMainTableColDefs";

export const statusAtExecutedRange = [
  ExecutionStrategyStatus.Executed,
  ExecutionStrategyStatus.ExecutedRPChangeRequested,
  ExecutionStrategyStatus.ExecutedRPAgreeChange,
  ExecutionStrategyStatus.ExecutedOPChangeRequested,

  //ER status for checking executed counterparty from OP perspective
  ExecutionRequestStatus.Executed,
  ExecutionRequestStatus.ExecutedPend,
  ExecutionRequestStatus.ExecutedRPChangeRequested,
  ExecutionRequestStatus.ExecutedPendRPChangeRequested,
  ExecutionRequestStatus.ExecutedRPAgreeChange,
  ExecutionRequestStatus.ExecutedPendRPAgreeChange,
  ExecutionRequestStatus.ExecutedOPChangeRequested,
  ExecutionRequestStatus.ExecutedPendOPChangeRequested,
  ExecutionRequestStatus.Executed,
];

export const statusAtExecutedAndAwardedRange = [
  ...statusAtExecutedRange,
  ExecutionStrategyStatus.Awarded,
  ExecutionRequestStatus.Awarded,
];

export const statusAtAffirmedRange = [
  ExecutionStrategyStatus.Affirmed,
  // ExecutionStrategyStatus.Complete,
  ExecutionRequestStatus.Affirmed,
  ExecutionRequestStatus.AffirmedPend,
];

export const statusFormExecutedToAffirmedRange = [
  // used for old  synRepoStatusToShowRepoRates
  ...statusAtExecutedRange,
  ...statusAtAffirmedRange,
];

export const statusFromAwarded = [
  ExecutionStrategyStatus.Working, // Order specific (Similar to Awarded)
  ExecutionStrategyStatus.NoMoreFills, // Orders specific.
  ExecutionStrategyStatus.Filled, // Order specific (Similar to Awarded)
  ExecutionStrategyStatus.ChangePending, // Order specific (Similar to Awarded)
  ExecutionStrategyStatus.Awarded,
  ...statusFormExecutedToAffirmedRange,
];

// LQC-2860 - OP statuses at which the Edit Panel may be opened. Shared by the
// right-click menu and the auto-close in handleRedrawingRowsForActionSummaryTable
// so they cannot drift: a status the menu refuses also closes an open panel.
export const OP_EDIT_PANEL_STATUSES_RFQ: string[] = [
  ExecutionStrategyStatus.Sent,
  ExecutionStrategyStatus.ReSent,
  ExecutionStrategyStatus.Acknowledged,
  ExecutionStrategyStatus.Recalled,
  ExecutionStrategyStatus.OffQuote,
  ExecutionStrategyStatus.Quoted,
  ExecutionStrategyStatus.ReQuoteRequested,
];

export const OP_EDIT_PANEL_STATUSES_ORDER: string[] = [
  ExecutionStrategyStatus.Sent,
  ExecutionStrategyStatus.Received,
  ExecutionStrategyStatus.Recalled,
  ExecutionStrategyStatus.ReSent,
];

// Unknown trade type falls back to either list, matching the menu's old check.
export function isEditPanelAllowedAtStatus(
  status: string | undefined,
  tradeType?: string,
): boolean {
  if (!status) return false;
  if (tradeType === TradeType.Order)
    return OP_EDIT_PANEL_STATUSES_ORDER.includes(status);
  if (tradeType === TradeType.RFQ)
    return OP_EDIT_PANEL_STATUSES_RFQ.includes(status);
  return (
    OP_EDIT_PANEL_STATUSES_RFQ.includes(status) ||
    OP_EDIT_PANEL_STATUSES_ORDER.includes(status)
  );
}

export const statusToShowDealtPrice = [
  ExecutionStrategyStatus.Executed,
  ExecutionStrategyStatus.NoMoreFills, // Orders specific.
  ExecutionStrategyStatus.Filled, // Orders specific.
  ExecutionStrategyStatus.Affirmed,
];

export const statusFromReceivedToQuoted = [
  ExecutionRequestStatus.Received,
  ExecutionRequestStatus.ReQuoteRequested,
  ExecutionRequestStatus.Recalled,
  ExecutionRequestStatus.InvalidQuote,
  ExecutionRequestStatus.NotDealt,
  ExecutionRequestStatus.Quoted,
  ExecutionRequestStatus.ReSent,
  // New status to fix NPV pack issues.
  ExecutionRequestStatus.Acknowledged,
  ExecutionRequestStatus.Rejected,
  ExecutionRequestStatus.Recalled,
  ExecutionRequestStatus.QuoteExpired,
  ExecutionStrategyStatus.QuoteExpired,
  ExecutionRequestStatus.OffQuote,
  ExecutionRequestStatus.Sent,
  //recoupon, recouponrequested?
];

// Statuses that mean a counterparty is no longer a valid participant in the trade
export const eliminatedStatuses: ExecutionRequestStatus[] = [
  ExecutionRequestStatus.Recalled,
  ExecutionRequestStatus.NotDealt,
  ExecutionRequestStatus.DealtAway,
  ExecutionRequestStatus.Rejected,
  ExecutionRequestStatus.Pulled,
  ExecutionRequestStatus.Closed,
  ExecutionRequestStatus.Archived,
  ExecutionRequestStatus.OffQuote,
  ExecutionRequestStatus.InvalidQuote,
  ExecutionRequestStatus.QuotedExpired,
  ExecutionRequestStatus.QuoteExpired,
];

/**
 * Helper function for formatting quote values (bid, ask, mid) in AG Grid cells
 * Handles null/undefined values and applies decimal formatting based on notation
 */
export function quoteValueFormatter(params: any): string {
  if (params.value && params.value != "") {
    let notation;

    // Check if this is from the main table (ES level) by checking for tableLevel in cellRendererParams
    if (
      params.colDef?.cellRendererParams &&
      typeof params.colDef.cellRendererParams === "function"
    ) {
      const rendererParams = params.colDef.cellRendererParams(params);
      if (rendererParams?.tableLevel === "ES") {
        notation = getNotationForMainTable(
          params,
          rendererParams.fullTradeData,
        );
      }
    }

    // Fallback to original logic for ER/Leg tables
    if (!notation) {
      notation = params.data.notation
        ? params.data.notation
        : params.data.notationER;
    }

    // Don't want null or undefined to come back as zero.
    if (notation != "$fr") {
      const valueForFormatter = returnDecimalFormatting(
        Number(params.value),
        notation,
        true,
      );

      return valueForFormatter;
    }
    if (notation == "$fr") {
      if (params.value.includes("-")) {
        return params.value;
      } else {
        const valueForFormatter = returnDecimalFormatting(
          Number(params.value),
          notation,
          true,
        );

        return valueForFormatter;
      }
    }
  }
  return "";
}

export function getQuoteTypeFromBlockId(fullData: any, blockId: any) {
  const executionRequestCounterParties =
    fullData.executionRequestCounterParties;
  let quoteType = "";

  executionRequestCounterParties.forEach((cp: any) => {
    const legs = cp.legs;
    legs.forEach((leg: any) => {
      if (leg.blockId === blockId) {
        quoteType = cp.quoteType;
      }
    });
  });
  return quoteType;
}

/**
 * Returns true if a leg's quotedSize differs from its size (notionalLeg1).
 * Blank/missing quotedSize is treated as "no difference" — the % Quoted column
 * should not be triggered by an unanswered quote.
 */
export function legHasReducedQuotedSize(leg: any): boolean {
  if (!leg) return false;
  const quotedSize = leg.quotedSize;
  if (quotedSize === null || quotedSize === undefined || quotedSize === "") {
    return false;
  }
  return Number(quotedSize) !== Number(leg.notionalLeg1);
}

/**
 * Returns true if any leg in the trade has a reduced quoted size — used to
 * decide whether the % Quoted column appears in the table.
 *
 * - OP: scans every counterparty's legs (column shows table-wide if any CP has a diff)
 * - RP: scans the trade's own legs
 */
export function tradeHasReducedQuotedSize(fullTradeData: any): boolean {
  if (!fullTradeData) return false;

  const counterParties = fullTradeData.executionRequestCounterParties ?? [];
  for (const cp of counterParties) {
    if (cp.legs?.some(legHasReducedQuotedSize)) return true;
  }

  if (fullTradeData.legs?.some(legHasReducedQuotedSize)) return true;

  return false;
}

/**
 * LQC-2849: % Quoted is RFQ-only. An Order leg's quotedSize tracks its fills,
 * so the reduced-size check alone would reveal the column on a partial fill.
 */
export function shouldShowPercentQuoted(
  tradeType: TradeType | string | undefined,
  fullTradeData: any,
): boolean {
  if (tradeType === TradeType.Order) return false;
  return tradeHasReducedQuotedSize(fullTradeData);
}

/**
 * LQC-1110: Returns true when a CP has the backend-driven showIceberg flag set.
 * Previous behaviour gated on releaseSize < 1 — replaced because the column
 * must also appear when releaseSize === 1 but the CP is iceberg-eligible.
 */
export function cpHasIceberg(cp: any): boolean {
  return cp?.showIceberg === true;
}

/**
 * LQC-1110: Returns true if any CP on the trade has showIceberg === true.
 * Drives the Iceberg column gating in executionRequestColDefsOP. OP-only;
 * RP path is intentionally not scanned.
 */
export function tradeHasIceberg(fullTradeData: any): boolean {
  if (!fullTradeData) return false;
  const counterParties = fullTradeData.executionRequestCounterParties ?? [];
  return counterParties.some(cpHasIceberg);
}
